import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Loader2, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import { API, errorText } from '@/utils/api';

// The customer-facing account report, on screen. It draws the SAME dict the
// PDF prints (/analytics/region-product-mix/account-report), so what the team
// sees and what the customer receives can never disagree.

// ₹ in Indian units — ₹28.39 L / ₹1.20 Cr / ₹84,500
const inr = (v, dec = 0) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: dec, minimumFractionDigits: dec })}`;
export const inrCompact = (v) => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  const s = n < 0 ? '−' : '';
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `${s}₹${(a / 1e5).toFixed(2)} L`;
  return `${s}${inr(a)}`;
};
const DEFAULT_LABELS = {
  noun: 'purchases', Noun: 'Purchases', total: 'Total purchases',
  quality_title: 'Quality-wise purchases', quality_sub: 'Every quality bought, by category, largest first.',
};
const num = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const STEADY = 2;

const tone = (pct) => (pct == null ? 'flat' : pct >= STEADY ? 'up' : pct <= -STEADY ? 'down' : 'flat');
const TONE_TEXT = { up: 'text-emerald-600', down: 'text-rose-600', flat: 'text-slate-500' };
const TONE_ON_DARK = { up: 'text-emerald-300', down: 'text-rose-300', flat: 'text-slate-400' };

function Change({ pct, onDark = false, suffix = '', money = null, className = '' }) {
  if (pct == null) return null;
  const t = tone(pct);
  const Icon = t === 'up' ? TrendingUp : t === 'down' ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${onDark ? TONE_ON_DARK[t] : TONE_TEXT[t]} ${className}`}>
      <Icon size={14} />
      {money != null && <>{money >= 0 ? '+' : '−'}{inr(Math.abs(money))}/m · </>}
      {pct > 0 ? '+' : ''}{pct.toFixed(1)}%{suffix}
    </span>
  );
}

// Highlight segments → spans; key figures bold and coloured.
function Segments({ parts }) {
  return parts.map((p, i) => (p.b || p.c)
    ? <strong key={i} className="font-bold" style={{ color: p.c || '#0F172A' }}>{p.t}</strong>
    : <span key={i}>{p.t}</span>);
}

function SectionTitle({ children, sub }) {
  return (
    <div className="mb-4">
      <h3 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h3>
      {sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

// light → full colour, so a ladder darkens as the price climbs
const tint = (hex, t) => {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c => Math.round(c + (255 - c) * t));
  return `rgb(${r},${g},${b})`;
};

function Bar({ pct, colour, ghost = null, height = 'h-3' }) {
  return (
    <div className="w-full">
      <div className={`w-full ${height} rounded-full bg-slate-100 overflow-hidden`}>
        <div className={`${height} rounded-full transition-all duration-700`} style={{ width: `${Math.max(0.8, Math.min(100, pct))}%`, background: colour }} />
      </div>
      {ghost != null && (
        <div className="w-full h-1.5 rounded-full mt-1">
          <div className="h-1.5 rounded-full bg-slate-300" style={{ width: `${Math.max(0.5, Math.min(100, ghost))}%` }} />
        </div>
      )}
    </div>
  );
}

export default function ProductMixAccountReport({ selectedDivision, periodType, periodValue, account = null,
  region = 'overall', onLoaded }) {
  const [reps, setReps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/analytics/region-product-mix/report`, {
      params: {
        division: selectedDivision !== 'all' ? selectedDivision : undefined,
        period_type: periodType, period_value: periodValue,
        ...(account ? { scope_kind: account.kind, scope: account.key } : { region }),
      },
      withCredentials: true,
    }).then(r => { if (live) { setReps(r.data.reports || []); onLoaded?.(r.data.reports || []); } })
      .catch(e => { if (live) setErr(errorText(e) || 'Could not load the report'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [selectedDivision, periodType, periodValue, account?.kind, account?.key, region]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-slate-500 gap-2"><Loader2 className="animate-spin" size={18} /> Building the report…</div>;
  }
  if (err) return <div className="py-16 text-center text-rose-600 text-sm">{err}</div>;
  if (!reps?.length) return null;
  return (
    <div className="space-y-16">
      {reps.map((r, i) => (
        <div key={`${r.account?.kind}-${r.account?.label}`} id={`pm-report-${i}`} className="scroll-mt-4">
          <AccountReportView rep={r} />
        </div>
      ))}
    </div>
  );
}

// Product Mix: where each quality sits on price inside its category — share
// bar, ₹/m dot on one scale, the category average as the premium / value
// divide, and last year's ghost dot with the move. Same dict as the PDF track.
function PriceTrack({ q, hasLy }) {
  const [all, setAll] = useState(false);
  const rows = all ? q.rows : q.rows.slice(0, 12);
  const rates = [...rows.map(r => r.avg_rate), ...(hasLy ? rows.map(r => r.ly_avg_rate) : []), q.avg_rate].filter(Boolean);
  if (!rates.length) return null;
  let lo = Math.min(...rates), hi = Math.max(...rates);
  const pad = Math.max((hi - lo) * 0.08, hi * 0.03, 1);
  lo = Math.max(0, lo - pad); hi += pad;
  const X = (v) => `${((v - lo) / (hi - lo)) * 100}%`;
  const topShare = Math.max(1, ...rows.map(r => r.share_cat));
  const moveCol = (t) => (t === 'up' ? '#16A34A' : t === 'down' ? '#DC2626' : '#64748B');
  return (
    <div className="px-6 pt-5 pb-3">
      <div className="grid grid-cols-[minmax(8rem,12rem)_minmax(7rem,11rem)_1fr] gap-x-4 text-[10px] font-bold tracking-[0.14em] text-slate-400 uppercase pb-2">
        <div />
        <div>Share of the category</div>
        <div className="relative flex justify-between">
          <span>‹ value</span>
          {q.avg_rate && <span className="absolute -translate-x-1/2 normal-case tracking-normal text-xs" style={{ left: X(q.avg_rate), color: q.colour }}>average {inr(q.avg_rate)}</span>}
          <span>premium ›</span>
        </div>
      </div>
      <div className="relative">
        {rows.map(r => {
          const mv = r.rate?.trend;
          const ly = hasLy ? r.ly_avg_rate : null;
          return (
            <div key={r.name} className="grid grid-cols-[minmax(8rem,12rem)_minmax(7rem,11rem)_1fr] gap-x-4 items-center h-8 group">
              <div className="text-sm font-semibold text-slate-800 text-right truncate" title={r.name}>{r.name}</div>
              <div className="flex items-center gap-2">
                <div className="h-2.5 rounded-full" style={{ width: `${(r.share_cat / topShare) * 75}%`, minWidth: 3, background: q.colour }} />
                <span className="text-xs font-bold tabular-nums text-slate-700">{r.share_cat.toFixed(0)}%</span>
              </div>
              <div className="relative h-8" style={{ background: `linear-gradient(90deg, ${tint(q.colour, 0.94)} 0%, ${tint(q.colour, 0.94)} ${q.avg_rate ? X(q.avg_rate) : '100%'}, ${tint(q.colour, 0.86)} ${q.avg_rate ? X(q.avg_rate) : '100%'}, ${tint(q.colour, 0.86)} 100%)` }}>
                {q.avg_rate && <div className="absolute top-0 bottom-0 border-l border-dashed" style={{ left: X(q.avg_rate), borderColor: q.colour }} />}
                {r.avg_rate && ly && (
                  <div className="absolute top-1/2 h-0.5 -translate-y-1/2" style={{ left: X(Math.min(ly, r.avg_rate)), width: `calc(${X(Math.max(ly, r.avg_rate))} - ${X(Math.min(ly, r.avg_rate))})`, background: moveCol(mv) }} />
                )}
                {r.avg_rate && ly && (
                  <div className="absolute top-1/2 w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white border-2" style={{ left: X(ly), borderColor: moveCol(mv) }}
                    title={`Last year ${inr(ly)}/m`} />
                )}
                {r.avg_rate && (() => {
                  // the label goes on the side away from last year's dot, and inside the track near its right end
                  const left = (ly && ly > r.avg_rate) || (r.avg_rate - lo) / (hi - lo) > 0.8;
                  return (
                    <div className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-1 ${left ? 'flex-row-reverse -translate-x-full' : ''}`}
                      style={{ left: X(r.avg_rate) }}>
                      <span className={`w-3.5 h-3.5 rounded-full ring-2 ring-white shadow ${left ? '-mr-[7px]' : '-ml-[7px]'}`} style={{ background: q.colour }} />
                      <span className="text-xs font-bold tabular-nums text-slate-800 bg-white/80 rounded px-1 whitespace-nowrap">{inr(r.avg_rate)}
                        {r.rate && <span className="ml-1 font-semibold" style={{ color: moveCol(mv) }}>{r.rate.pct > 0 ? '+' : ''}{r.rate.pct.toFixed(0)}%</span>}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
        {q.rows.length > 12 && (
          <button type="button" className="font-semibold text-indigo-600 hover:underline" onClick={() => setAll(v => !v)}>
            {all ? 'Show the largest 12' : `Show all ${q.rows.length} qualities`}
          </button>
        )}
        {hasLy && <span>Hollow dot: last year&rsquo;s ₹/m — <span className="text-emerald-600 font-semibold">green</span> trading up, <span className="text-rose-600 font-semibold">red</span> trading down.</span>}
      </div>
    </div>
  );
}

// Against last year, one category: rate (up / down) × metres (more / fewer)
// per quality, where the ₹/m move came from (mix vs price), and the
// qualities new this year or not bought again. Same dict as the PDF map.
const QUADS = {
  up_more: ['Higher rate · more metres', '#16A34A'], up_less: ['Higher rate · fewer metres', '#D97706'],
  down_more: ['Lower rate · more metres', '#0EA5E9'], down_less: ['Lower rate · fewer metres', '#DC2626'],
};

function YoyMap({ q }) {
  const [hot, setHot] = useState(null);
  const rows = q.rows.filter(r => r.rate && r.metres_change_pct != null).sort((a, b) => b.value - a.value).slice(0, 14);
  const [xmax, ymax] = q.yoy.map_scale || [20, 8];
  const W = 1080, H = 380, x0 = 52, x1 = W - 12, y0 = 24, y1 = H - 34;
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  const X = (v) => cx + Math.max(-1, Math.min(1, v / xmax)) * (x1 - x0) / 2;
  const Y = (v) => cy - Math.max(-1, Math.min(1, v / ymax)) * (y1 - y0) / 2;
  const vmax = Math.max(1, ...rows.map(r => r.value));
  const rad = (r) => 5 + 15 * Math.sqrt(r.value / vmax);
  // corner captions count as taken, so no quality label lands on one
  const placed = Object.entries(QUADS).map(([k, [label]]) => {
    const up = k.startsWith('up'), more = k.endsWith('more'), w = label.length * 7.6;
    const tx = more ? x1 - 8 - w : x0 + 8, ty = up ? y0 + 15 : y1 - 8;
    return [tx, ty - 11, tx + w, ty + 3];
  });
  const labels = rows.map(r => {
    const px = X(r.metres_change_pct), py = Y(r.rate.pct), rr = rad(r);
    const tw = r.name.length * 6.4;
    const right = px < x1 - 90;
    const lx = right ? px + rr + 4 : px - rr - 4 - tw;
    for (const dy of [0, -12, 12, -24, 24]) {
      const b = [lx - 1, py - 8 + dy, lx + tw + 1, py + 5 + dy];
      if (placed.every(p => b[2] < p[0] || b[0] > p[2] || b[3] < p[1] || b[1] > p[3])) {
        placed.push(b);
        return { r, x: lx, y: py + 4 + dy };
      }
    }
    return null;
  }).filter(Boolean);
  const col = q.colour;
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none">
        {Object.entries(QUADS).map(([k, [label, hx]]) => {
          const up = k.startsWith('up'), more = k.endsWith('more');
          return (
            <g key={k}>
              <rect x={more ? cx : x0} y={up ? y0 : cy} width={(x1 - x0) / 2} height={(y1 - y0) / 2} fill={tint(hx, 0.9)} />
              <text x={more ? x1 - 8 : x0 + 8} y={up ? y0 + 15 : y1 - 8} textAnchor={more ? 'end' : 'start'}
                fontSize="10" fontWeight="700" letterSpacing="0.08em" fill={hx}>{label.toUpperCase()}</text>
            </g>
          );
        })}
        <line x1={x0} x2={x1} y1={cy} y2={cy} stroke="#94A3B8" />
        <line x1={cx} x2={cx} y1={y0} y2={y1} stroke="#94A3B8" />
        {[-1, -0.5, 0.5, 1].map(k => (
          <g key={k} fontSize="10" fill="#64748B">
            <text x={X(xmax * k)} y={y1 + 14} textAnchor="middle">{`${xmax * k > 0 ? '+' : ''}${(xmax * k).toFixed(0)}%`}</text>
            <text x={x0 - 6} y={Y(ymax * k) + 3} textAnchor="end">{`${ymax * k > 0 ? '+' : ''}${(ymax * k).toFixed(0)}%`}</text>
          </g>
        ))}
        <text x={cx} y={H - 4} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#64748B">metres against last year ›</text>
        <text x={x0} y={12} fontSize="10.5" fontWeight="700" fill="#64748B">‹ ₹/m against last year ›  (up = higher rate)</text>
        {[...rows].reverse().map(r => (
          <circle key={r.name} cx={X(r.metres_change_pct)} cy={Y(r.rate.pct)} r={rad(r)}
            fill={col} fillOpacity={hot == null || hot === r.name ? 0.6 : 0.18} stroke={col} strokeWidth="1.2"
            onMouseEnter={() => setHot(r.name)} onMouseLeave={() => setHot(null)} className="cursor-pointer" />
        ))}
        {labels.map(({ r, x, y }) => (
          <text key={r.name} x={x} y={y} fontSize="11" fontWeight="600" fill="#0F172A"
            opacity={hot == null || hot === r.name ? 1 : 0.35}>{r.name}</text>
        ))}
      </svg>
      {hot && (() => {
        const r = rows.find(x => x.name === hot);
        return (
          <div className="absolute top-2 right-2 rounded-xl bg-slate-900 text-slate-100 text-xs p-3 shadow-2xl w-64 pointer-events-none">
            <div className="font-bold text-sm text-white mb-1">{r.name}</div>
            <div className="flex justify-between"><span className="text-slate-400">₹/m</span>
              <span><b>{inr(r.avg_rate)}</b> vs {inr(r.ly_avg_rate)} · <span style={{ color: r.rate.pct >= 0 ? '#4ADE80' : '#F87171' }}>{r.rate.pct > 0 ? '+' : ''}{r.rate.pct.toFixed(1)}%</span></span></div>
            <div className="flex justify-between"><span className="text-slate-400">Metres</span>
              <span><b>{num(r.metres)}</b> vs {num(r.ly_metres)} · {r.metres_change_pct > 0 ? '+' : ''}{r.metres_change_pct.toFixed(0)}%</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Value</span>
              <span><b>{inrCompact(r.value)}</b> vs {inrCompact(r.ly_value)}</span></div>
          </div>
        );
      })()}
    </div>
  );
}

function AgainstLastYear({ q, L }) {
  const y = q.yoy;
  const trend = y.rate?.trend;
  const verdict = y.rate ? (L[trend] || L.steady) : 'No rate last year';
  const vCol = trend === 'up' ? '#16A34A' : trend === 'down' ? '#DC2626' : '#64748B';
  const sg = (v) => `${v >= 0 ? '+' : '−'}${inr(Math.abs(v))}`;
  const volWord = { more: 'more metres', less: 'fewer metres', same: 'about the same metres' }[y.volume];
  const parts = y.rate_delta && y.price_effect != null
    ? [['Mix', y.mix_effect, y.mix_effect >= 0 ? 'more of the dearer qualities' : 'more of the cheaper qualities'],
       ['Price', y.price_effect, y.price_effect >= 0 ? 'higher rates, same qualities' : 'lower rates, same qualities']]
    : [];
  const pmax = Math.max(1, ...parts.map(p => Math.abs(p[1])));
  const outfit = { fontFamily: 'Outfit, sans-serif' };
  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 text-white flex flex-wrap items-center gap-x-5 gap-y-2" style={{ background: `linear-gradient(90deg, ${q.colour}, ${shade(q.colour)})` }}>
        <h4 className="text-2xl font-bold" style={outfit}>{q.category}</h4>
        <span className="px-3 py-1 rounded-full bg-white text-sm font-bold" style={{ color: vCol }}>{verdict}</span>
        {y.rate && <span className="text-sm"><b className="text-lg" style={outfit}>{inr(y.avg_rate)}/m</b> vs {inr(y.ly_avg_rate)} ({y.rate.pct > 0 ? '+' : ''}{y.rate.pct.toFixed(1)}%)</span>}
        {y.metres_change_pct != null && <span className="text-sm">{y.volume !== 'same' ? `${Math.abs(y.metres_change_pct).toFixed(0)}% ` : ''}{volWord}</span>}
        {y.value_change_pct != null && <span className="text-sm opacity-90">value {y.value_change_pct > 0 ? '+' : ''}{y.value_change_pct.toFixed(0)}%</span>}
      </div>
      <div className="p-6 space-y-5">
        {parts.length > 0 && (
          <div>
            <div className="text-xs font-bold tracking-[0.14em] text-slate-400 uppercase mb-2">Where the {sg(y.rate_delta)}/m came from</div>
            <div className="space-y-2">
              {parts.map(([k, v, why]) => (
                <div key={k} className="grid grid-cols-[4rem_1fr_18rem] items-center gap-3">
                  <span className="text-sm font-semibold text-slate-700">{k}</span>
                  <div className="relative h-5 rounded-full bg-slate-100">
                    <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-300" />
                    <div className="absolute top-0.5 bottom-0.5 rounded-full" style={{
                      background: v >= 0 ? '#16A34A' : '#DC2626', width: `${(Math.abs(v) / pmax) * 50}%`,
                      left: v >= 0 ? '50%' : `${50 - (Math.abs(v) / pmax) * 50}%` }} />
                  </div>
                  <span className="text-sm tabular-nums"><b style={{ color: v >= 0 ? '#16A34A' : '#DC2626' }}>{sg(v)}</b> <span className="text-slate-500">{why}</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
        {q.rows.some(r => r.rate && r.metres_change_pct != null) && <YoyMap q={q} />}
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(QUADS).filter(([k]) => y.counts?.[k]).map(([k, [label, hx]]) => (
            <span key={k} className="px-2.5 py-1 rounded-full font-semibold" style={{ background: tint(hx, 0.88), color: hx }}>{y.counts[k]} · {label}</span>
          ))}
        </div>
        {(y.new?.length > 0 || y.dropped?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            {y.new?.length > 0 && (
              <div>
                <div className="text-xs font-bold tracking-[0.14em] text-violet-600 uppercase mb-1.5">New this year</div>
                <div className="flex flex-wrap gap-1.5">{y.new.map(n => (
                  <span key={n.name} className="px-2 py-0.5 rounded-md bg-violet-50 text-violet-800">{n.name} · {inrCompact(n.value)}</span>
                ))}</div>
              </div>
            )}
            {y.dropped?.length > 0 && (
              <div>
                <div className="text-xs font-bold tracking-[0.14em] text-slate-500 uppercase mb-1.5">Not bought this year</div>
                <div className="flex flex-wrap gap-1.5">{y.dropped.map(n => (
                  <span key={n.name} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{n.name} · {inrCompact(n.ly_value)} last year</span>
                ))}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// The report itself, drawn from the account-report dict.
export function AccountReportView({ rep }) {
  const cats = useMemo(() => (rep?.categories || []).filter(c => c.value > 0), [rep]);
  const L = rep.labels || DEFAULT_LABELS;
  const t = rep.totals;
  const hasLy = rep.yoy?.available;
  const rateMax = Math.max(1, ...cats.map(c => Math.max(c.avg_rate || 0, c.ly?.avg_rate || 0)));
  const changeMax = Math.max(1, ...rep.categories.map(c => Math.abs(c.change_value || 0)));

  return (
    <div className="max-w-[1500px] mx-auto space-y-10 pb-10">
      {/* ── Masthead + headline figures ── */}
      <section className="rounded-3xl overflow-hidden shadow-xl"
        style={{ background: 'linear-gradient(120deg, #0F172A 0%, #1E1B4B 55%, #4C1D95 100%)' }}>
        <div className="px-8 pt-8 pb-6">
          <div className="text-[11px] font-bold tracking-[0.2em] text-violet-300 uppercase">
            Product mix · {rep.division}{rep.voice === 'team' && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 tracking-[0.12em] text-violet-200">Team view</span>}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-1 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {rep.account?.label}
          </h2>
          <div className="text-slate-300 mt-1 text-sm">
            {[rep.account?.sub, rep.period?.label, rep.period?.span !== rep.period?.label && rep.period?.span].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-white/10">
          {[
            { label: L.total, value: inrCompact(t.value), accent: true,
              chip: hasLy && <Change pct={t.value_change_pct} onDark suffix={rep.yoy?.span ? ` vs ${rep.yoy.span}` : ''} /> },
            { label: 'Metres', value: `${num(t.metres)} m`, chip: hasLy && <Change pct={t.metres_change_pct} onDark /> },
            { label: 'Average rate', value: t.avg_rate ? `${inr(t.avg_rate)}/m` : '—',
              chip: hasLy && t.rate && <Change pct={t.rate.pct} money={t.rate.delta} onDark /> },
            { label: 'Qualities', value: num(t.qualities) },
          ].map((k, i) => (
            <div key={k.label} className={`px-8 py-6 ${i ? 'lg:border-l border-white/10' : ''}`}>
              <div className="text-[11px] font-bold tracking-[0.16em] text-slate-400 uppercase">{k.label}</div>
              <div className={`mt-1 font-bold tracking-tight ${k.accent ? 'text-5xl text-amber-200' : 'text-4xl text-white'}`}
                style={{ fontFamily: 'Outfit, sans-serif' }}>{k.value}</div>
              <div className="mt-1 text-sm">{k.chip}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Category cards ── */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {rep.categories.map(c => (
          <div key={c.name} className="rounded-2xl p-6 text-white shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all"
            style={{ background: `linear-gradient(135deg, ${c.colour} 0%, ${shade(c.colour)} 100%)` }}>
            <div className="text-xs font-bold tracking-[0.18em] uppercase opacity-90">{c.name}</div>
            <div className="text-4xl font-bold mt-1 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{inrCompact(c.value)}</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.share.toFixed(1)}%</span>
              <span className="text-sm opacity-90">of {L.noun}</span>
            </div>
            <div className="mt-1 text-sm font-medium opacity-95">{c.avg_rate ? `${inr(c.avg_rate)}/m` : '—'} · {num(c.metres)} m</div>
            {hasLy && (
              <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-xs font-bold backdrop-blur">
                {c.ly && !c.ly.value ? 'New this year'
                  : c.change_pct != null && <>{c.change_pct > 0 ? <TrendingUp size={13} /> : c.change_pct < 0 ? <TrendingDown size={13} /> : <Minus size={13} />} {Math.abs(c.change_pct).toFixed(1)}% vs last year</>}
              </div>
            )}
          </div>
        ))}
      </section>

      {/* ── Highlights ── */}
      {rep.highlights?.length > 0 && (
        <section className="rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="text-violet-600" size={22} />
            <h3 className="text-4xl text-violet-700" style={{ fontFamily: 'Caveat, cursive', fontWeight: 600 }}>Highlights</h3>
          </div>
          <ul className="grid lg:grid-cols-2 gap-x-10 gap-y-3">
            {rep.highlights.map((h, i) => (
              <li key={i} className="flex gap-3 text-[17px] leading-relaxed text-slate-700">
                <span className="mt-2.5 h-2 w-2 rounded-full bg-violet-500 flex-none" />
                <span><Segments parts={h} /></span>
              </li>
            ))}
          </ul>
          {!hasLy && rep.yoy?.note && <p className="mt-5 text-xs text-slate-500">{rep.yoy.note}</p>}
        </section>
      )}

      {/* ── Category mix + rates ── */}
      {cats.length > 0 && (
        <section className="grid xl:grid-cols-5 gap-6">
          <div className="xl:col-span-2 rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
            <SectionTitle sub={`Share of ${L.noun} by category`}>Category mix</SectionTitle>
            <div className="relative h-72">
              {/* No entry animation: inside the opening dialog it froze part-way (2026-10-29,
                  only an arc of the donut drew). The ring is drawn whole, at once. */}
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 288 }}>
                <PieChart>
                  <Pie data={cats} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="62%" outerRadius="95%"
                    paddingAngle={2} stroke="#fff" strokeWidth={3} isAnimationActive={false}>
                    {cats.map(c => <Cell key={c.name} fill={c.colour} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [inrCompact(v), n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{inrCompact(t.value)}</div>
                <div className="text-[10px] font-bold tracking-[0.18em] text-slate-500 uppercase">{L.total}</div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {cats.map(c => (
                <div key={c.name} className="flex items-center gap-3">
                  <span className="h-3.5 w-3.5 rounded" style={{ background: c.colour }} />
                  <span className="font-semibold w-24" style={{ color: c.colour }}>{c.name}</span>
                  <span className="text-xl font-bold text-slate-900 w-20" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.share.toFixed(1)}%</span>
                  <span className="text-sm text-slate-500">{inrCompact(c.value)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="xl:col-span-3 rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
            <SectionTitle sub={`What each metre cost on average${hasLy ? ' — the grey bar is the same months last year' : ''}`}>Average rate per metre</SectionTitle>
            <div className="space-y-5">
              {cats.filter(c => c.avg_rate).map(c => (
                <div key={c.name} className="grid grid-cols-[110px_1fr_auto] items-center gap-4">
                  <span className="font-semibold text-slate-800">{c.name}</span>
                  <Bar pct={c.avg_rate / rateMax * 100} colour={`linear-gradient(90deg, ${tint(c.colour, 0.35)}, ${c.colour})`}
                    ghost={hasLy && c.ly?.avg_rate ? c.ly.avg_rate / rateMax * 100 : null} height="h-4" />
                  <div className="text-right min-w-[150px]">
                    <div className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{inr(c.avg_rate)}/m</div>
                    {hasLy && c.rate && <Change pct={c.rate.pct} money={c.rate.delta} className="text-xs" />}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-7 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                    {['Category', L.Noun, 'Share', 'Metres', 'Avg ₹/m', ...(hasLy ? ['Last year', 'Change', 'Rate move'] : [])].map((h, i) => (
                      <th key={h} className={`px-3 py-2.5 font-semibold ${i ? 'text-right' : 'text-left'} ${i === 0 ? 'rounded-l-lg' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rep.categories.map(c => {
                    const move = c.rate ? (c.rate.trend === 'up' ? 'Trading up' : c.rate.trend === 'down' ? 'Trading down' : 'Steady') : '—';
                    return (
                      <tr key={c.name} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2.5 font-bold" style={{ color: c.colour }}>{c.name}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{inr(c.value)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{c.share.toFixed(1)}%</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{num(c.metres)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{c.avg_rate ? inr(c.avg_rate) : '—'}</td>
                        {hasLy && <>
                          <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{inr(c.ly?.value)}</td>
                          <td className="px-3 py-2.5 text-right">{c.ly && !c.ly.value ? <span className="font-bold text-violet-600">NEW</span> : <Change pct={c.change_pct} />}</td>
                          <td className={`px-3 py-2.5 text-right font-bold ${move === 'Trading up' ? 'text-emerald-600' : move === 'Trading down' ? 'text-rose-600' : 'text-slate-500'}`}>{move}</td>
                        </>}
                      </tr>
                    );
                  })}
                  <tr className="bg-slate-100 font-bold text-slate-900">
                    <td className="px-3 py-2.5 rounded-l-lg">Total</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{inr(t.value)}</td>
                    <td className="px-3 py-2.5 text-right">100%</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{num(t.metres)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{t.avg_rate ? inr(t.avg_rate) : '—'}</td>
                    {hasLy && <>
                      <td className="px-3 py-2.5 text-right tabular-nums">{inr(t.ly?.value)}</td>
                      <td className="px-3 py-2.5 text-right"><Change pct={t.value_change_pct} /></td>
                      <td />
                    </>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ── What changed (YoY) ── */}
      {hasLy && rep.categories.some(c => c.change_value) && (
        <section className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
          <SectionTitle sub="Each category's rupee change against the same months last year">What changed since last year</SectionTitle>
          <div className="space-y-3">
            {rep.categories.filter(c => c.change_value).map(c => {
              const w = Math.abs(c.change_value) / changeMax * 36;   // room left for the label
              const up = c.change_value >= 0;
              return (
                <div key={c.name} className="grid grid-cols-[110px_1fr] items-center gap-4">
                  <span className="font-semibold text-slate-800">{c.name}</span>
                  <div className="relative h-9">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300" />
                    <div className={`absolute top-1.5 h-6 rounded-md ${up ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' : 'bg-gradient-to-l from-rose-400 to-rose-600'}`}
                      style={up ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }} />
                    <span className={`absolute top-1.5 text-sm font-bold ${up ? 'text-emerald-700' : 'text-rose-700'}`}
                      style={up ? { left: `calc(50% + ${w}% + 8px)` } : { right: `calc(50% + ${w}% + 8px)` }}>
                      {up ? '+' : ''}{inrCompact(c.change_value)} {c.change_pct != null ? `(${c.change_pct > 0 ? '+' : ''}${c.change_pct.toFixed(1)}%)` : '(new)'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── GG: CTG (invoice category) mix ── */}
      {(rep.ctg || []).some(c => c.value > 0) && (
        <section className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
          <SectionTitle sub={`Share of ${L.noun} by the invoice's CTG (category), with the average rate per metre${hasLy ? ' — the grey bar is last year' : ''}`}>
            Category (CTG) mix
          </SectionTitle>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {rep.ctg.filter(c => c.value > 0).map(c => (
              <div key={c.ctg} className="rounded-2xl p-5 text-white shadow-md" style={{ background: `linear-gradient(135deg, ${c.colour} 0%, ${shade(c.colour)} 100%)` }}>
                <div className="text-xs font-bold tracking-[0.18em] uppercase opacity-90">CTG {c.ctg}</div>
                <div className="text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{inrCompact(c.value)}</div>
                <div className="text-sm font-medium mt-1">{c.share.toFixed(1)}% · {num(c.metres)} m · {c.avg_rate ? `${inr(c.avg_rate)}/m` : '—'}</div>
                {hasLy && c.change_pct != null && (
                  <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-xs font-bold">
                    {c.change_pct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {Math.abs(c.change_pct).toFixed(1)}% vs last year
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="space-y-3">
            {rep.ctg.map(c => {
              const max = Math.max(1, ...rep.ctg.map(x => Math.max(x.share, x.ly?.share || 0)));
              return (
                <div key={c.ctg} className="grid grid-cols-[90px_1fr_auto] items-center gap-4">
                  <span className="font-bold" style={{ color: c.colour }}>CTG {c.ctg}</span>
                  <Bar pct={c.share / max * 100} colour={c.colour} ghost={hasLy ? (c.ly?.share || 0) / max * 100 : null} />
                  <span className="text-sm font-bold text-slate-900 tabular-nums min-w-[70px] text-right">{c.share.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Price ladders ── */}
      {rep.ladders?.length > 0 && (
        <section>
          <SectionTitle sub={`${L.Noun} by realised rate per metre, cheapest first — the colour deepens as the price climbs`}>Price ladders</SectionTitle>
          <div className="grid xl:grid-cols-2 2xl:grid-cols-3 gap-6">
            {rep.ladders.map(l => (
              <div key={l.category} className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <h4 className="text-2xl font-bold" style={{ color: l.colour, fontFamily: 'Outfit, sans-serif' }}>{l.category}</h4>
                  <div className="text-right">
                    <span className="text-xs uppercase tracking-wider text-slate-500 mr-1.5">average</span>
                    <span className="text-2xl font-bold" style={{ color: l.colour, fontFamily: 'Outfit, sans-serif' }}>{l.avg_rate ? `${inr(l.avg_rate)}/m` : '—'}</span>
                    {hasLy && l.rate && <div><Change pct={l.rate.pct} money={l.rate.delta} suffix=" vs last year" className="text-xs" /></div>}
                  </div>
                </div>
                <div className="mt-5 space-y-3.5">
                  {l.bands.map((b, j) => {
                    const top = Math.max(1, ...l.bands.map(x => Math.max(x.share, x.ly_share || 0)));
                    return (
                      <div key={b.band} className="grid grid-cols-[128px_1fr_auto] items-center gap-3">
                        <span className="text-sm font-semibold text-slate-700 tabular-nums whitespace-nowrap">{b.band}/m</span>
                        <Bar pct={b.share / top * 100} colour={tint(l.colour, 0.7 - 0.7 * (j / Math.max(1, l.bands.length - 1)))}
                          ghost={hasLy ? (b.ly_share || 0) / top * 100 : null} />
                        <div className="text-right min-w-[118px]">
                          <span className="font-bold text-slate-900 tabular-nums">{b.share.toFixed(1)}%</span>
                          <span className="text-xs text-slate-500 ml-1.5">{b.value ? inrCompact(b.value) : 'none'}</span>
                          {hasLy && b.delta_pp != null && Math.abs(b.delta_pp) >= 0.5 && (
                            <div className="text-[11px] font-semibold text-slate-600">{b.delta_pp > 0 ? '▲' : '▼'} {Math.abs(b.delta_pp).toFixed(1)} pts</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-xs text-slate-500">
                  Sweet spot <strong style={{ color: l.colour }}>{l.sweet_spot}/m</strong> — {l.sweet_spot_share.toFixed(0)}% of {l.category}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Against last year: rate × volume per category ── */}
      {rep.qualities?.some(q => q.yoy) && (
        <section>
          <SectionTitle sub={L.yoy_sub}>{L.yoy_title}</SectionTitle>
          <div className="grid min-[1800px]:grid-cols-2 gap-6">
            {rep.qualities.filter(q => q.yoy).map(q => <AgainstLastYear key={q.category} q={q} L={L} />)}
          </div>
        </section>
      )}

      {/* ── Qualities ── */}
      {rep.qualities?.length > 0 && (
        <section>
          <SectionTitle sub={L.quality_sub}>{L.quality_title}</SectionTitle>
          <div className="grid min-[1800px]:grid-cols-2 gap-6">
            {rep.qualities.map(q => {
              const top = Math.max(1, ...q.rows.map(r => r.share_cat));
              return (
                <div key={q.category} className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 text-white flex items-baseline justify-between" style={{ background: `linear-gradient(90deg, ${q.colour}, ${shade(q.colour)})` }}>
                    <h4 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{q.category}</h4>
                    <span className="text-sm font-semibold opacity-95">{q.rows.length} qualities · {inrCompact(q.value)}</span>
                  </div>
                  <PriceTrack q={q} hasLy={hasLy} />
                  <details className="border-t border-slate-100 group">
                    <summary className="px-6 py-2.5 text-xs font-semibold text-indigo-600 cursor-pointer select-none hover:bg-slate-50">
                      Full table — all {q.rows.length} qualities with metres{hasLy ? ' and last year' : ''}
                    </summary>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          {['#', 'Quality', 'Metres', L.Noun, 'Share', 'Avg ₹/m', ...(hasLy ? ['Last year', 'Change'] : [])].map((h, i) => (
                            <th key={h} className={`px-3 py-2 font-semibold ${i > 1 ? 'text-right' : 'text-left'} ${h === 'Share' ? 'w-[26%]' : ''}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {q.rows.map((r, k) => (
                          <tr key={r.name} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-400 tabular-nums">{k + 1}</td>
                            <td className="px-3 py-2 font-semibold text-slate-800">{r.name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{num(r.metres)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">{inr(r.value)}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className="w-12 text-right tabular-nums text-slate-700">{r.share_cat.toFixed(1)}%</span>
                                <Bar pct={r.share_cat / top * 100} colour={q.colour} height="h-2.5" />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.avg_rate ? inr(r.avg_rate) : '—'}</td>
                            {hasLy && <>
                              <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.ly_value ? inr(r.ly_value) : '—'}</td>
                              <td className="px-3 py-2 text-right">{r.new ? <span className="font-bold text-violet-600">NEW</span> : <Change pct={r.change_pct} />}</td>
                            </>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </details>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {rep.order_gap?.length > 0 && (
        <section className="rounded-3xl bg-white border border-amber-200 p-7 shadow-sm">
          <SectionTitle sub="Qualities whose share of orders runs ahead of their share of dispatches, and qualities ordered but not dispatched. Internal: order value is estimated and coverage partial — a pointer, not a figure to quote.">
            Ordered ahead of dispatched
          </SectionTitle>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                {['Quality', 'Dispatched %', 'Ordered %', 'Gap', 'Reading'].map((h, i) => (
                  <th key={h} className={`px-3 py-2 font-semibold ${i && i < 4 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rep.order_gap.map(g => (
                <tr key={g.trade_name} className="border-b border-slate-100">
                  <td className="px-3 py-2 font-semibold text-slate-800">{g.trade_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{g.dispatched_pct.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{g.ordered_pct.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">+{g.gap_pp.toFixed(1)} pts</td>
                  <td className={`px-3 py-2 font-bold ${g.dispatched ? 'text-amber-600' : 'text-rose-600'}`}>{g.dispatched ? 'under-supplied' : 'ordered, not dispatched'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <div className="text-xs text-slate-500 space-y-0.5">
        {(rep.notes || []).map(n => <p key={n}>{n}</p>)}
      </div>
    </div>
  );
}

// a deeper shade of the colour for gradient ends
function shade(hex) {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c => Math.round(c * 0.72));
  return `rgb(${r},${g},${b})`;
}

// ── Compare: head to head in the report style ────────────────────────────
export const CMP_A = '#4F46E5';
export const CMP_B = '#D97706';
const CATS = ['Shirting', 'Suiting', 'Ethnic', 'Other'];
const CAT_COLOURS = { Shirting: '#0EA5E9', Suiting: '#7C3AED', Ethnic: '#EC4899', Other: '#14B8A6' };
const reading = (v) => (v >= 75 ? 'Very alike' : v >= 50 ? 'Broadly similar' : 'Quite different');
const bandFloor = (label) => {
  if (!label || label === 'Unpriced') return 1e9;
  if (label.startsWith('≤')) return 0;
  const m = label.match(/([\d,]+)/);
  return m ? parseInt(m[1].replace(/,/g, ''), 10) : 1e9;
};

function PairRow({ label, a, b, aText, bText, max, labelW = 'w-28' }) {
  return (
    <div className="flex items-center gap-4">
      <span className={`${labelW} shrink-0 text-sm font-semibold text-slate-700 tabular-nums whitespace-nowrap`}>{label}</span>
      <div className="flex-1 space-y-1">
        {[[a, aText, CMP_A], [b, bText, CMP_B]].map(([v, txt, c], i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
              {v > 0 && <div className="h-3 rounded-full transition-all duration-700" style={{ width: `${Math.max(1, v / max * 100)}%`, background: c }} />}
            </div>
            <span className="w-36 text-xs font-bold tabular-nums" style={{ color: v > 0 ? c : '#94A3B8' }}>{v > 0 ? txt : 'none'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CompareHeadToHead({ res, metric = 'value' }) {
  const ra = res.reports?.a;
  const rb = res.reports?.b;
  if (!ra || !rb) return null;
  const A = ra.account?.label;
  const B = rb.account?.label;
  const cv = res.compare?.[metric] || {};
  const tiles = [
    ['Quality overlap', cv.qualities?.overlap, 'same qualities, same proportions'],
    ['Category overlap', cv.fabrics?.rows?.length ? cv.fabrics?.overlap : null, 'Shirting / Suiting / Ethnic / Other'],
    ['Shirting prices', cv.bands?.Shirting?.overlap, 'realised ₹/m bands'],
    ['Suiting prices', cv.bands?.Suiting?.overlap, 'realised ₹/m bands'],
  ].filter(([, v]) => v != null);
  const ca = Object.fromEntries(ra.categories.map(c => [c.name, c]));
  const cb = Object.fromEntries(rb.categories.map(c => [c.name, c]));
  const names = CATS.filter(n => ca[n] || cb[n]);
  const shareMax = Math.max(1, ...names.map(n => Math.max(ca[n]?.share || 0, cb[n]?.share || 0)));
  const la = Object.fromEntries(ra.ladders.map(l => [l.category, l]));
  const lb = Object.fromEntries(rb.ladders.map(l => [l.category, l]));
  const ladderCats = ['Shirting', 'Suiting', 'Ethnic'].filter(n => la[n] || lb[n]);
  const outfit = { fontFamily: 'Outfit, sans-serif' };

  return (
    <div className="space-y-8">
      <section className="rounded-3xl overflow-hidden shadow-xl" style={{ background: 'linear-gradient(120deg, #0F172A 0%, #1E1B4B 60%, #312E81 100%)' }}>
        <div className="px-8 pt-7 pb-4">
          <div className="text-[11px] font-bold tracking-[0.2em] text-violet-300 uppercase">Compare · product mix · {ra.division}</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-1 tracking-tight" style={outfit}>
            <span style={{ color: '#A5B4FC' }}>{A}</span> <span className="text-slate-400 font-normal text-2xl">vs</span> <span style={{ color: '#FCD34D' }}>{B}</span>
          </h2>
          <div className="text-slate-300 text-sm mt-1">{[ra.period?.label, ra.period?.span !== ra.period?.label && ra.period?.span].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="grid grid-cols-[150px_1fr_1fr] text-white border-t border-white/10">
          <div />
          {[[ra, '#A5B4FC', CMP_A], [rb, '#FCD34D', CMP_B]].map(([r, hx, bar], i) => (
            <div key={i} className="px-6 py-3 border-l border-white/10" style={{ boxShadow: `inset 0 3px 0 ${bar}` }}>
              <div className="text-lg font-bold" style={{ color: hx, ...outfit }}>{r.account?.label}</div>
              <div className="text-[11px] text-slate-400">{r.account?.sub}</div>
            </div>
          ))}
          {[
            ['Total dispatches', r => inrCompact(r.totals.value)],
            ['Metres', r => `${num(r.totals.metres)} m`],
            ['Average rate', r => (r.totals.avg_rate ? `${inr(r.totals.avg_rate)}/m` : '—')],
            ['Qualities', r => num(r.totals.qualities)],
          ].map(([label, f]) => (
            <React.Fragment key={label}>
              <div className="px-6 py-3 border-t border-white/10 text-[11px] font-bold tracking-[0.14em] text-slate-400 uppercase flex items-center">{label}</div>
              {[ra, rb].map((r, i) => (
                <div key={i} className="px-6 py-3 border-t border-l border-white/10 text-3xl font-bold" style={outfit}>{f(r)}</div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </section>

      {tiles.length > 0 && (
        <section className="grid grid-cols-2 xl:grid-cols-4 gap-5">
          {tiles.map(([title, v, hint]) => (
            <div key={title} className="rounded-2xl p-5 text-white shadow-lg"
              style={{ background: v >= 75 ? 'linear-gradient(135deg,#7C3AED,#5B21B6)' : v >= 50 ? 'linear-gradient(135deg,#6366F1,#4338CA)' : 'linear-gradient(135deg,#F43F5E,#BE123C)' }}>
              <div className="text-xs font-bold tracking-[0.16em] uppercase opacity-90">{title}</div>
              <div className="text-4xl font-bold mt-1" style={outfit}>{v.toFixed(0)}%</div>
              <div className="text-sm font-semibold mt-1">{reading(v)}</div>
              <div className="text-xs opacity-90">{hint}</div>
            </div>
          ))}
        </section>
      )}

      {res.highlights?.length > 0 && (
        <section className="rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-amber-50 p-7 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="text-violet-600" size={22} />
            <h3 className="text-4xl text-violet-700" style={{ fontFamily: 'Caveat, cursive', fontWeight: 600 }}>Head to head</h3>
          </div>
          <ul className="grid lg:grid-cols-2 gap-x-10 gap-y-3">
            {res.highlights.map((h, i) => (
              <li key={i} className="flex gap-3 text-[16px] leading-relaxed text-slate-700">
                <span className="mt-2.5 h-2 w-2 rounded-full bg-violet-500 flex-none" />
                <span><Segments parts={h} /></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {names.length > 0 && (
        <section className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
          <SectionTitle sub="Share of dispatches and the average rate per metre on each side">Category mix, side by side</SectionTitle>
          <div className="flex gap-4 text-xs font-semibold mb-4">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CMP_A }} />{A}</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CMP_B }} />{B}</span>
          </div>
          <div className="grid xl:grid-cols-2 gap-x-10 gap-y-4">
            {names.map(n => (
              <PairRow key={n} label={<span style={{ color: CAT_COLOURS[n] }}>{n}</span>} max={shareMax}
                a={ca[n]?.share || 0} b={cb[n]?.share || 0}
                aText={`${(ca[n]?.share || 0).toFixed(1)}% · ${ca[n]?.avg_rate ? inr(ca[n].avg_rate) : '—'}/m`}
                bText={`${(cb[n]?.share || 0).toFixed(1)}% · ${cb[n]?.avg_rate ? inr(cb[n].avg_rate) : '—'}/m`} />
            ))}
          </div>
        </section>
      )}

      {(() => {
        // GG: CTG (invoice category) mix, side by side.
        const ga = Object.fromEntries((ra.ctg || []).filter(c => c.value > 0).map(c => [c.ctg, c]));
        const gb = Object.fromEntries((rb.ctg || []).filter(c => c.value > 0).map(c => [c.ctg, c]));
        const keys = [...new Set([...Object.keys(ga), ...Object.keys(gb)])]
          .sort((x, y) => Math.max(gb[y]?.value || 0, ga[y]?.value || 0) - Math.max(gb[x]?.value || 0, ga[x]?.value || 0));
        if (!keys.length) return null;
        const max = Math.max(1, ...keys.map(k => Math.max(ga[k]?.share || 0, gb[k]?.share || 0)));
        return (
          <section className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
            <SectionTitle sub="Share of dispatches by the invoice's CTG (category) and its average rate per metre">Category (CTG) mix, side by side</SectionTitle>
            <div className="grid xl:grid-cols-2 gap-x-10 gap-y-4">
              {keys.map(k => (
                <PairRow key={k} label={`CTG ${k}`} max={max} a={ga[k]?.share || 0} b={gb[k]?.share || 0}
                  aText={`${(ga[k]?.share || 0).toFixed(1)}% · ${ga[k]?.avg_rate ? inr(ga[k].avg_rate) : '—'}/m`}
                  bText={`${(gb[k]?.share || 0).toFixed(1)}% · ${gb[k]?.avg_rate ? inr(gb[k].avg_rate) : '—'}/m`} />
              ))}
            </div>
          </section>
        );
      })()}

      {ladderCats.length > 0 && (
        <section>
          <SectionTitle sub="Share of each category's dispatches by realised ₹/m band, cheapest first">Price ladders, side by side</SectionTitle>
          <div className="grid xl:grid-cols-2 2xl:grid-cols-3 gap-6">
            {ladderCats.map(n => {
              const ba = Object.fromEntries((la[n]?.bands || []).map(b => [b.band, b]));
              const bb = Object.fromEntries((lb[n]?.bands || []).map(b => [b.band, b]));
              const bands = [...new Set([...Object.keys(ba), ...Object.keys(bb)])]
                .filter(x => ba[x]?.share || bb[x]?.share).sort((x, y) => bandFloor(x) - bandFloor(y));
              const max = Math.max(1, ...bands.map(x => Math.max(ba[x]?.share || 0, bb[x]?.share || 0)));
              return (
                <div key={n} className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
                  <div className="flex items-baseline justify-between">
                    <h4 className="text-2xl font-bold" style={{ color: CAT_COLOURS[n], ...outfit }}>{n}</h4>
                    <div className="text-sm font-bold">
                      <span style={{ color: CMP_A }}>{la[n]?.avg_rate ? inr(la[n].avg_rate) : '—'}/m</span>
                      <span className="text-slate-400 mx-1.5">vs</span>
                      <span style={{ color: CMP_B }}>{lb[n]?.avg_rate ? inr(lb[n].avg_rate) : '—'}/m</span>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3">
                    {bands.map(x => (
                      <PairRow key={x} label={`${x}/m`} max={max} labelW="w-32"
                        a={ba[x]?.share || 0} b={bb[x]?.share || 0}
                        aText={`${(ba[x]?.share || 0).toFixed(1)}%`} bText={`${(bb[x]?.share || 0).toFixed(1)}%`} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Customer report cover (Orders & Dispatch) — same kit, same data as the PDF ──
export function ReportCover({ cover, customer, period, extras = [] }) {
  if (!cover) return null;
  const outfit = { fontFamily: 'Outfit, sans-serif' };
  const oo = cover.orders;
  const ds = cover.dispatches;
  const band = (tiles) => (
    <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-white/10">
      {tiles.map(([label, value, sub], i) => (
        <div key={label} className={`px-7 py-5 ${i ? 'lg:border-l border-white/10' : ''}`}>
          <div className="text-[11px] font-bold tracking-[0.16em] text-slate-400 uppercase">{label}</div>
          <div className={`mt-1 font-bold tracking-tight ${i === 0 ? 'text-4xl text-amber-200' : 'text-3xl text-white'}`} style={outfit}>{value}</div>
          {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
        </div>
      ))}
    </div>
  );
  return (
    <div className="space-y-6">
      <section className="rounded-3xl overflow-hidden shadow-xl" style={{ background: 'linear-gradient(120deg, #0F172A 0%, #1E1B4B 55%, #4C1D95 100%)' }}>
        <div className="px-7 pt-6 pb-4">
          <div className="text-[11px] font-bold tracking-[0.2em] text-violet-300 uppercase">{cover.title} · {customer?.division}</div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-1 tracking-tight" style={outfit}>{customer?.name}</h2>
          <div className="text-slate-300 mt-1 text-sm">{[period, customer?.location, (customer?.codes || []).join(', '), customer?.region, ...extras].filter(Boolean).join(' · ')}</div>
        </div>
        {oo && band([
          ['Units ordered this period', num(oo.units_ordered), `${num(oo.lines)} order lines`],
          ['Dispatched against them', num(oo.units_dispatched)],
          ['Pending', num(oo.units_pending)],
          ['Fill', oo.fill_pct != null ? `${Math.round(oo.fill_pct)}%` : '—'],
        ])}
        {ds && band([
          ['Net value', inrCompact(ds.net_value), `${num(ds.invoices)} invoices · excl. GST`],
          ['Metres', `${num(ds.metres)} m`, `${num(ds.pieces)} pieces`],
          ['Average rate', ds.avg_rate ? `${inr(ds.avg_rate)}/m` : '—', 'net of discounts'],
          ['Pieces', num(ds.pieces)],
        ])}
      </section>
      {cover.cards?.length > 0 && (
        <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {cover.cards.map(c => (
            <div key={c.name} className="rounded-2xl p-5 text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${c.colour} 0%, ${shade(c.colour)} 100%)` }}>
              <div className="text-xs font-bold tracking-[0.18em] uppercase opacity-90">{c.name}</div>
              <div className="text-3xl font-bold mt-1" style={outfit}>
                {ds && c.net_value ? inrCompact(c.net_value) : c.units_ordered ? `${num(c.units_ordered)} units ordered` : inrCompact(0)}
              </div>
              {ds && c.net_value > 0 && <div className="text-sm font-medium mt-1 opacity-95">{c.share_metres != null ? `${Math.round(c.share_metres)}% of metres · ` : ''}{num(c.metres)} m · {c.avg_rate ? `${inr(c.avg_rate)}/m` : '—'}</div>}
              {oo && c.units_ordered > 0 && <div className="text-sm font-semibold mt-1">{num(c.units_pending)} of {num(c.units_ordered)} units pending</div>}
            </div>
          ))}
        </section>
      )}
      {cover.returns_note && <p className="text-xs text-slate-500 -mt-3">{cover.returns_note}</p>}
      {cover.highlights?.length > 0 && (
        <section className="rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="text-violet-600" size={20} />
            <h3 className="text-3xl text-violet-700" style={{ fontFamily: 'Caveat, cursive', fontWeight: 600 }}>Highlights</h3>
          </div>
          <ul className="grid lg:grid-cols-2 gap-x-10 gap-y-2.5">
            {cover.highlights.map((h, i) => (
              <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-slate-700">
                <span className="mt-2 h-2 w-2 rounded-full bg-violet-500 flex-none" />
                <span><Segments parts={h} /></span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ── Order & dispatch composition (customer report) ─────────────────────────
// Draws payload.composition — the same dict the PDF's mirrored charts print.
// Its own job, distinct from Product Mix: what was ORDERED against what was
// SENT, quality by quality, with fill. Names, fabrics and ₹/m are Product
// Mix's, so the numbers agree with Sales Analytics.
const fillTone = (f) => (f == null ? '#94A3B8' : f >= 90 ? '#16A34A' : f >= 50 ? '#D97706' : '#DC2626');

function FillRing({ pct, size = 30 }) {
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  const p = pct == null ? 0 : Math.min(100, pct);
  return (
    <svg width={size} height={size} className="shrink-0" aria-label={pct == null ? 'no order' : `${p.toFixed(0)}% filled`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth="3.2" />
      {pct != null && p > 0 && (
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={fillTone(p)} strokeWidth="3.2" strokeLinecap="round"
          strokeDasharray={`${(c * p) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      )}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize="9" fontWeight="700"
        fill={pct == null ? '#94A3B8' : '#0F172A'}>{pct == null ? '—' : p.toFixed(0)}</text>
    </svg>
  );
}

// Drawn on document.body at the row's screen position, so no card or dialog
// edge can clip it; opens above the row when there is no room below.
function QualityTip({ r, rect, showOrders, showDispatches }) {
  if (!rect) return null;
  const below = window.innerHeight - rect.bottom > 190;
  const left = Math.min(Math.max(8, rect.left + rect.width / 2 - 144), window.innerWidth - 296);
  const pos = below ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 };
  return createPortal(
    <div style={{ position: 'fixed', left, ...pos, zIndex: 9999 }} className="w-72 rounded-xl bg-slate-900 text-slate-100 shadow-2xl p-3 text-xs pointer-events-none">
      <div className="font-bold text-sm text-white mb-1.5 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.colour }} />{r.name}
      </div>
      {r.others && <div className="text-slate-400 mb-1.5 leading-snug">{(r.names || []).join(', ')}</div>}
      {showOrders && (
        <div className="flex justify-between py-0.5"><span className="text-slate-400">Ordered</span>
          <span><b>{num(r.units_ordered)}</b> units{r.order_pct != null && <> · {r.order_pct.toFixed(1)}%</>}</span></div>
      )}
      {showOrders && r.units_ordered > 0 && (
        <div className="flex justify-between py-0.5"><span className="text-slate-400">Dispatched against them</span>
          <span><b>{num(r.units_dispatched)}</b> · {num(r.units_ordered - r.units_dispatched)} pending</span></div>
      )}
      {showDispatches && (
        <>
          <div className="flex justify-between py-0.5"><span className="text-slate-400">Sent</span>
            <span><b>{num(r.metres)} m</b>{r.dispatch_pct != null && <> · {r.dispatch_pct.toFixed(1)}%</>} · {num(r.pieces)} pcs</span></div>
          <div className="flex justify-between py-0.5"><span className="text-slate-400">Value</span>
            <span><b>{inrCompact(r.net_value)}</b>{r.value_pct != null && <> · {r.value_pct.toFixed(1)}%</>}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-slate-400">Average rate</span>
            <span><b>{r.avg_rate ? `${inr(r.avg_rate)}/m` : '—'}</b></span></div>
        </>
      )}
      {r.returned_metres > 0 && (
        <div className="flex justify-between py-0.5 text-rose-300"><span>Returned</span>
          <span>{num(r.returned_metres)} m · {inrCompact(r.returned_value)}</span></div>
      )}
    </div>,
    document.body,
  );
}

function CompositionFabric({ b, showOrders, showDispatches }) {
  const [hot, setHot] = useState(null);
  const [tipRect, setTipRect] = useState(null);
  const byValue = showDispatches && b.net_value > 0;
  const slices = b.rows.map((r, i) => ({ i, name: r.name, v: (byValue ? r.net_value : r.units_ordered) || 0, colour: r.colour }))
    .filter(s => s.v > 0);
  const vmax = Math.max(1e-9, ...b.rows.flatMap(r => [showOrders ? r.order_pct || 0 : 0, showDispatches ? r.dispatch_pct || 0 : 0]));
  const outfit = { fontFamily: 'Outfit, sans-serif' };
  const both = showOrders && showDispatches;
  const grid = both ? 'grid-cols-[1fr_minmax(9rem,13rem)_1fr_4.5rem_2.5rem]'
    : showDispatches ? 'grid-cols-[minmax(9rem,13rem)_1fr_4.5rem]' : 'grid-cols-[1fr_minmax(9rem,13rem)_2.5rem]';
  return (
    <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm">
      <div className="px-6 py-4 flex flex-wrap items-baseline gap-x-6 gap-y-1"
        style={{ background: `linear-gradient(110deg, ${b.colour} 0%, ${tint(b.colour, 0.25)} 70%, ${tint(b.colour, 0.45)} 100%)` }}>
        <div className="text-2xl font-bold text-white tracking-tight" style={outfit}>{b.fabric}</div>
        {showDispatches && b.net_value > 0 && (
          <div className="text-white/95 text-sm"><b className="text-lg" style={outfit}>{inrCompact(b.net_value)}</b>
            {b.share_value != null && <> · {b.share_value.toFixed(0)}% of value</>} · {num(b.metres)} m
            {b.avg_rate ? <> · <b>{inr(b.avg_rate)}/m</b></> : null}</div>
        )}
        {showOrders && b.units_ordered > 0 && (
          <div className="text-white/95 text-sm">{num(b.units_ordered)} units ordered · <b>{(b.fill_pct ?? 0).toFixed(0)}% dispatched</b>
            {b.units_pending > 0 && <> · {num(b.units_pending)} pending</>}</div>
        )}
        <div className="text-white/80 text-xs ml-auto">{b.qualities} qualit{b.qualities === 1 ? 'y' : 'ies'}</div>
      </div>
      <div className="grid lg:grid-cols-[15rem_1fr] gap-4 p-5 items-center">
        <div className={`relative ${b.rows.length <= 2 ? 'h-40' : 'h-56 lg:h-60'}`}>
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 240, height: 240 }}>
            <PieChart>
              <Pie data={slices.length ? slices : [{ name: '—', v: 1, colour: '#E2E8F0', i: -1 }]} dataKey="v" nameKey="name"
                innerRadius="60%" outerRadius="92%" paddingAngle={slices.length > 1 ? 1.5 : 0} stroke="none" isAnimationActive={false}
                onMouseEnter={(_, idx) => setHot(slices[idx]?.i ?? null)} onMouseLeave={() => setHot(null)}>
                {(slices.length ? slices : [{ colour: '#E2E8F0', i: -1 }]).map(s => (
                  <Cell key={s.i} fill={s.colour} opacity={hot == null || hot === s.i ? 1 : 0.25} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {hot != null && b.rows[hot] ? (
              <>
                <div className="text-xs font-semibold text-slate-500 max-w-[8rem] text-center truncate">{b.rows[hot].name}</div>
                <div className="text-2xl font-bold text-slate-900" style={outfit}>
                  {((byValue ? b.rows[hot].value_pct : b.rows[hot].order_pct) ?? 0).toFixed(0)}%</div>
                <div className="text-[10px] tracking-widest text-slate-400 uppercase">of {byValue ? 'value' : 'units'}</div>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-slate-900" style={outfit}>{byValue ? inrCompact(b.net_value) : num(b.units_ordered)}</div>
                <div className="text-[10px] tracking-widest text-slate-400 uppercase">{byValue ? 'value split' : 'units split'}</div>
              </>
            )}
          </div>
        </div>
        <div className="min-w-0">
          <div className={`grid ${grid} gap-x-3 px-2 pb-2 text-[10px] font-bold tracking-[0.14em] text-slate-400 uppercase`}>
            {showOrders && <div className="text-right">‹ Ordered · share of units</div>}
            <div className="text-center">Quality</div>
            {showDispatches && <div>Dispatched · share of metres ›</div>}
            {showDispatches && <div className="text-center">₹/m</div>}
            {showOrders && <div className="text-center">Fill</div>}
          </div>
          {b.rows.map((r, i) => {
            const on = hot === i;
            const dim = hot != null && !on;
            return (
              <div key={r.name} onMouseEnter={(e) => { setHot(i); setTipRect(e.currentTarget.getBoundingClientRect()); }}
                onMouseLeave={() => { setHot(null); setTipRect(null); }}
                className={`relative grid ${grid} gap-x-3 items-center px-2 py-1.5 rounded-xl transition-colors ${on ? 'bg-slate-100' : i % 2 ? '' : 'bg-slate-50/70'} ${dim ? 'opacity-50' : ''}`}>
                {showOrders && (
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs font-bold tabular-nums text-slate-700">{r.order_pct ? `${r.order_pct.toFixed(0)}%` : '—'}</span>
                    <div className="h-4 rounded-l-md rounded-r-sm transition-all duration-700"
                      style={{ width: `${((r.order_pct || 0) / vmax) * 82}%`, minWidth: r.order_pct ? 3 : 0, background: tint(r.colour, 0.45) }} />
                  </div>
                )}
                <div className="flex flex-col items-center min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0 max-w-full">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.colour }} />
                    <span className={`text-sm truncate ${r.others ? 'text-slate-500 italic' : 'font-semibold text-slate-800'}`}>{r.name}</span>
                  </div>
                  {r.returned_metres > 0 && <span className="text-[10px] text-rose-600">−{num(r.returned_metres)} m returned</span>}
                </div>
                {showDispatches && (
                  <div className="flex items-center gap-2">
                    <div className="h-4 rounded-r-md rounded-l-sm transition-all duration-700"
                      style={{ width: `${((r.dispatch_pct || 0) / vmax) * 82}%`, minWidth: r.dispatch_pct ? 3 : 0, background: r.colour }} />
                    <span className="text-xs font-bold tabular-nums text-slate-700">{r.dispatch_pct ? `${r.dispatch_pct.toFixed(0)}%` : '—'}</span>
                  </div>
                )}
                {showDispatches && (
                  <div className="text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold tabular-nums"
                      style={{ background: r.avg_rate ? tint(b.colour, 0.84) : '#F1F5F9', color: r.avg_rate ? '#0F172A' : '#94A3B8' }}>
                      {r.avg_rate ? inr(r.avg_rate) : '—'}
                    </span>
                  </div>
                )}
                {showOrders && <div className="flex justify-center"><FillRing pct={r.fill_pct} /></div>}
                {on && <QualityTip r={r} rect={tipRect} showOrders={showOrders} showDispatches={showDispatches} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function OrderDispatchComposition({ comp }) {
  const fabrics = comp?.fabrics || [];
  if (!fabrics.length || !(comp.show_orders || comp.show_dispatches)) return null;
  const sides = [comp.show_orders && 'ordered (share of units) on the left', comp.show_dispatches && 'dispatched (share of metres) on the right']
    .filter(Boolean).join(' and ');
  return (
    <section>
      <SectionTitle sub={`Quality by quality inside each fabric — ${sides}. Hover a quality for its figures.`}>
        What was ordered, what was sent
      </SectionTitle>
      <div className="space-y-6">
        {fabrics.map(b => <CompositionFabric key={b.fabric} b={b} showOrders={comp.show_orders} showDispatches={comp.show_dispatches} />)}
      </div>
      <p className="text-xs text-slate-500 mt-3">
        Shares are within each fabric. ₹/m is the average net rate on invoice lines that carry metres.
        {comp.show_orders && ' Fill is the units of this period’s orders dispatched to date.'}
        {' '}Sales Analytics reports the same qualities net of goods returns.
      </p>
    </section>
  );
}
